import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utils
import { parseFontAwesomeClass } from '../../../utils/fontawesome/config';
import cn from '../../../utils/cn';

// Styles
import styles from './DashboardItemList.module.css';

/**
 * Dashboard item list component for displaying a list of items with icons and colors in dashboard
 * @param {object} props
 * @param {Array<ApiDashboardListItem> | null} [props.items] - Array of list items
 * @param {string} [props.placeholder='-'] - Placeholder for empty values
 * @returns {JSX.Element}
 */
function DashboardItemList({
  items = null,
  placeholder = '-',
}) {
  const itemsArray = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  if (itemsArray.length === 0) {
    return (
      <div className={styles['dashboard-item-list']}>
        <span className={styles['dashboard-item-list-empty']}>{placeholder}</span>
      </div>
    );
  }

  return (
    <div className={styles['dashboard-item-list']}>
      <div className={styles['dashboard-item-list-items']}>
        {itemsArray.map((item, index) => {
          const label = item.lib || '';
          const iconDefinition = item?.icon ? parseFontAwesomeClass(item.icon) : null;
          const iconColor = item?.iconColor || 'transparent';
          const backgroundColor = item?.backgroundColor || 'transparent';
          const path = item?.path || '';

          const containerClassName = cn([
            styles['dashboard-item-list-item-container'],
            styles['dashboard-item-list-item-container-background-color'],
          ]);

          const containerContent = (
            <>
              {iconDefinition ? (
                <FontAwesomeIcon icon={/** @type {any} */(iconDefinition)} />
              ) : null}
              <span className={styles['dashboard-item-list-label']}>{label}</span>
            </>
          );

          // Create a unique key from item properties
          const itemKey = `${item.lib || ''}-${item.path || ''}-${item.icon || ''}-${item.iconColor || ''}-${item.backgroundColor || ''}`;

          return (
            <div key={itemKey || `item-${index}`} className={styles['dashboard-item-list-item']}>
              {path ? (
                <a
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={containerClassName}
                  style={{ color: iconColor, backgroundColor }}
                >
                  {containerContent}
                </a>
              ) : (
                <div
                  className={containerClassName}
                  style={{ color: iconColor, backgroundColor }}
                >
                  {containerContent}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DashboardItemList;
